import { ActionIcon, Box, Tooltip, UnstyledButton } from '@mantine/core';
import { IconChevronUp } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { componentsTestIds } from '@components/testids';
import { WidgetDefinition, WidgetType } from '../../../Widgets/types';
import { widgetGlyph, widgetGroupGlyph } from '../../../Widgets/WidgetIcons/glyphs';
import {
  groupWidgetTypesByFunction,
  widgetDescriptionFallback,
  widgetDescriptionKey,
  widgetGroupLabelFallback,
  widgetGroupLabelKey,
  widgetLabelFallback,
  widgetLabelKey
} from '../../../widgetCatalog';
import classes from './CollapsedBar.module.css';

/**
 * The floating palette bar — a HOVER TRAY over five families (2026-08-19).
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * ## Three versions, and why this is the third
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * 1. **A flat carousel of every type.** It looked right — the ticket owner said so
 *    directly ("the previous was better in that way") — because a row of tiles
 *    reads as a TOOL PALETTE: things you pick up, not options you choose from. Its
 *    defect was structural rather than visual: the strip's width tracked the number
 *    of widget types, so it could only ever grow.
 * 2. **Five dropdown menus.** Fixed the growth and organised the types sensibly,
 *    and lost the palette entirely: five labelled buttons that open menus is a MENU
 *    BAR. "Well organized, but not quite there aesthetically."
 * 3. **This.** Five compact family buttons; hovering one raises a TRAY of that
 *    family's tiles above the bar. The width is fixed by the family count, the
 *    tiles come back, and exploring costs a pointer movement rather than a click.
 *    Chosen from `prototypes/prototype-d-palette-bar.html` variant 2.
 *
 * ## ⚠️ The tray is a HOVER affordance, so it needs three non-obvious things
 *
 *  - **`:focus-within` as well as `:hover`** (in the CSS). Without it the tray is
 *    reachable by Tab but invisible while focused — worse than unreachable, since
 *    focus lands somewhere the user cannot see. Same rule the card chrome follows.
 *  - **The bridge.** A tray floating 9px above its button leaves a gap the pointer
 *    crosses on its way up; without something occupying it the tray closes
 *    mid-travel. `.grp::after` fills it, invisibly.
 *  - **The chevron survives.** Hover is not available on touch, and a tray shows a
 *    name but not the one-line description. The full palette — the ONLY surface
 *    that explains what each type is for — stays one click away, exactly where it
 *    was, on the ticket owner's instruction.
 *
 * ## Why grouped by function, not by data source
 *
 * `widgetCatalog` carries both axes and they genuinely disagree. `source` answers
 * "where does this read from", which decides which CONFIGURATION fields you get and
 * is the right axis for the inspector. `group` answers "what does this look like",
 * which is the question someone opening a palette is actually asking — `kpi` (Cube)
 * and `count` (readmodel) are both one big number and belong together here even
 * though they configure completely differently.
 */
type Props = {
  opened: boolean;
  onOpen: () => void;
  widgets: WidgetType[];
  onAddWidget: ( widgetDefinition: Partial<WidgetDefinition> ) => void;
};

export default function CollapsedBar({ opened, onOpen, widgets, onAddWidget }: Props ) {
  const { t } = useTranslation( 'widgets' );

  // Empty groups are dropped by the catalog helper, so a group this device offers
  // no types for cannot render as an empty family button with an empty tray.
  const groups = groupWidgetTypesByFunction( widgets );

  if ( opened ) return null;
  return (
    <Box className={classes.collapsedRoot}>
      <div className={classes.groups}>
        {groups.map(({ group, types }) => (
          <div key={group} className={classes.group}>
            {/*
              NOT a button that opens anything. It is the tray's hover target and
              its label — which is why it is a `div` with no click handler: a
              control that looks pressable and does nothing when pressed is worse
              than one that plainly is not. Clicking still does something useful
              though: it falls through to the tray, which is already open by then.
            */}
            <div className={classes.groupButton} data-testid={componentsTestIds.reportsPaletteGroup( group )}>
              <span className={classes.groupIcon}>{widgetGroupGlyph( group, 16 )}</span>
              <span className={classes.groupLabel}>
                {t( widgetGroupLabelKey( group ), widgetGroupLabelFallback( group ))}
              </span>
            </div>

            <div className={classes.tray} role="group">
              {types.map(( type ) => (
                <UnstyledButton
                  key={type}
                  className={classes.trayItem}
                  data-testid={componentsTestIds.reportsPaletteTrayItem( type )}
                  onClick={() => onAddWidget({ type })}
                  // The DESCRIPTION as the native tooltip. It is the one thing the
                  // tray cannot show without becoming a menu again, and the full
                  // palette behind the chevron is where it is shown properly — this
                  // is the cheap middle step, not a replacement for that.
                  title={t( widgetDescriptionKey( type ), widgetDescriptionFallback( type ))}
                >
                  <span className={classes.trayIcon}>{widgetGlyph( type, 20 )}</span>
                  <span className={classes.trayLabel}>
                    {t( widgetLabelKey( type ), widgetLabelFallback( type ))}
                  </span>
                </UnstyledButton>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Tooltip label={t( 'palette_drawer_open', 'Abrir la paleta de tarjetas' )}>
        <ActionIcon
          size="md"
          radius="xl"
          variant="default"
          className={classes.toggleArrow}
          data-testid={componentsTestIds.reportsPaletteOpen}
          onClick={onOpen}
          aria-label={t( 'palette_drawer_open', 'Abrir la paleta de tarjetas' )}
        >
          <IconChevronUp size={18} />
        </ActionIcon>
      </Tooltip>
    </Box>
  );
}
